import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Gem, UserPlus, Check, X, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";

const COUNTRY_CODES = [
  { c: "US", d: "+1" }, { c: "IN", d: "+91" }, { c: "GB", d: "+44" }, { c: "AE", d: "+971" },
  { c: "CA", d: "+1" }, { c: "AU", d: "+61" }, { c: "SG", d: "+65" }, { c: "PK", d: "+92" },
  { c: "BD", d: "+880" }, { c: "NG", d: "+234" }, { c: "ZA", d: "+27" }, { c: "PH", d: "+63" },
  { c: "ID", d: "+62" }, { c: "MY", d: "+60" }, { c: "SA", d: "+966" }, { c: "DE", d: "+49" },
  { c: "FR", d: "+33" }, { c: "IT", d: "+39" }, { c: "ES", d: "+34" }, { c: "BR", d: "+55" },
  { c: "MX", d: "+52" }, { c: "RU", d: "+7" }, { c: "CN", d: "+86" }, { c: "JP", d: "+81" },
  { c: "KR", d: "+82" }, { c: "TR", d: "+90" }, { c: "NP", d: "+977" }, { c: "LK", d: "+94" },
  { c: "KE", d: "+254" }, { c: "EG", d: "+20" },
];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", username: "", email: "", password: "", sponsor_code: "", position: "left", country_code: "+1", phone: "", otp: "" });
  const [busy, setBusy] = useState(false);
  const [placementLocked, setPlacementLocked] = useState(false);
  const [unameStatus, setUnameStatus] = useState(null); // {checking, available, reason}
  const [otpSent, setOtpSent] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [otpMasked, setOtpMasked] = useState("");

  const sendOtp = async () => {
    const email = form.email.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { toast.error("Enter a valid email first"); return; }
    setSendingOtp(true);
    try {
      const { data } = await api.post("/auth/register/request-otp", { email });
      setOtpSent(true);
      setOtpMasked(data.sent_to || "");
      toast.success(`Verification code sent to ${data.sent_to || "your email"}`);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not send code");
    }
    setSendingOtp(false);
  };

  // Live username availability check (debounced) — runs as the user types
  useEffect(() => {
    const u = form.username.trim().toLowerCase();
    if (!u) { setUnameStatus(null); return; }
    if (u.length < 3 || !/^[a-z0-9]+$/.test(u)) {
      setUnameStatus({ checking: false, available: false, reason: u.length < 3 ? "At least 3 characters" : "Letters & numbers only" });
      return;
    }
    setUnameStatus({ checking: true });
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/auth/username-available", { params: { u } });
        setUnameStatus({ checking: false, available: data.available, reason: data.reason });
      } catch {
        setUnameStatus(null);
      }
    }, 450);
    return () => clearTimeout(t);
  }, [form.username]);

  useEffect(() => {
    const ref = params.get("ref");
    const pos = params.get("pos");
    setForm((f) => ({
      ...f,
      ...(ref ? { sponsor_code: ref } : {}),
      ...(pos === "left" || pos === "right" ? { position: pos } : {}),
    }));
    if (pos === "left" || pos === "right") setPlacementLocked(true);
  }, [params]);

  // Auto-detect country dial code from visitor IP
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch("https://ipapi.co/json/");
        const j = await r.json();
        if (j?.country_calling_code) setForm((f) => ({ ...f, country_code: j.country_calling_code }));
      } catch { /* keep default */ }
    })();
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (unameStatus && unameStatus.available === false) {
      toast.error(`Username: ${unameStatus.reason}`);
      return;
    }
    if (!otpSent) { toast.error("Please verify your email — tap 'Send code'"); return; }
    if (!/^\d{6}$/.test((form.otp || "").trim())) { toast.error("Enter the 6-digit code sent to your email"); return; }
    setBusy(true);
    try {
      await register(form);
      toast.success("Account created! Welcome to MoneyMoonX");
      navigate("/portal");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Registration failed");
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#050507]">
      <div className="flex items-center justify-center p-6 order-2 lg:order-1">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-xl gold-btn grid place-items-center"><Gem className="w-5 h-5 text-black" /></div>
            <div className="font-display font-extrabold text-lg">Money<span className="gold-text">MoonX</span></div>
          </Link>
          <h1 className="font-display text-3xl font-bold">Start your journey</h1>
          <p className="text-slate-400 mt-2 text-sm">Create your investor account in seconds.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="text-sm text-slate-400">Full Name</label>
              <input data-testid="register-name-input" required value={form.name} onChange={set("name")}
                className="w-full mt-1.5 bg-[#0D0E12] border border-amber-500/20 focus:border-amber-500 rounded-xl px-4 py-3 outline-none" placeholder="John Doe" />
            </div>
            <div>
              <label className="text-sm text-slate-400">Username</label>
              <div className="relative">
                <input data-testid="register-username-input" required minLength={3} value={form.username} onChange={set("username")}
                  className={`w-full mt-1.5 bg-[#0D0E12] border rounded-xl px-4 py-3 pr-10 outline-none ${unameStatus == null || unameStatus.checking ? "border-amber-500/20 focus:border-amber-500" : unameStatus.available ? "border-emerald-500/60" : "border-red-500/60"}`}
                  placeholder="johndoe (letters & numbers)" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 mt-[3px]" data-testid="username-status-icon">
                  {unameStatus?.checking && <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />}
                  {unameStatus && !unameStatus.checking && unameStatus.available && <Check className="w-4 h-4 text-emerald-400" />}
                  {unameStatus && !unameStatus.checking && unameStatus.available === false && <X className="w-4 h-4 text-red-400" />}
                </span>
              </div>
              {unameStatus && !unameStatus.checking && (
                <p className={`text-xs mt-1 ${unameStatus.available ? "text-emerald-400" : "text-red-400"}`} data-testid="username-status-text">
                  {unameStatus.available ? "✓ Username available" : unameStatus.reason}
                </p>
              )}
            </div>
            <div>
              <label className="text-sm text-slate-400">Email</label>
              <div className="flex gap-2 mt-1.5">
                <input data-testid="register-email-input" type="email" required value={form.email}
                  onChange={(e) => { setForm({ ...form, email: e.target.value }); setOtpSent(false); }}
                  className="flex-1 bg-[#0D0E12] border border-amber-500/20 focus:border-amber-500 rounded-xl px-4 py-3 outline-none" placeholder="you@email.com" />
                <button type="button" data-testid="register-send-otp-btn" onClick={sendOtp} disabled={sendingOtp}
                  className="shrink-0 px-4 py-3 rounded-xl text-sm font-semibold border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 transition-colors disabled:opacity-50">
                  {sendingOtp ? "Sending..." : otpSent ? "Resend" : "Send code"}
                </button>
              </div>
            </div>
            {otpSent && (
              <div data-testid="register-otp-block">
                <label className="text-sm text-slate-400">Email Verification Code</label>
                <input data-testid="register-otp-input" inputMode="numeric" maxLength={6} required value={form.otp}
                  onChange={(e) => setForm({ ...form, otp: e.target.value.replace(/\D/g, "") })}
                  className="w-full mt-1.5 bg-[#0D0E12] border border-emerald-500/40 focus:border-emerald-500 rounded-xl px-4 py-3 outline-none font-mono-fin tracking-[6px] text-center text-lg" placeholder="000000" />
                <p className="text-xs text-emerald-400 mt-1">Enter the 6-digit code sent to {otpMasked || "your email"} (valid 10 min).</p>
              </div>
            )}
            <div>
              <label className="text-sm text-slate-400">Mobile Number</label>
              <div className="flex gap-2 mt-1.5">
                <select data-testid="register-country-code-select" value={form.country_code} onChange={set("country_code")}
                  className="bg-[#0D0E12] border border-amber-500/20 focus:border-amber-500 rounded-xl px-3 py-3 outline-none font-mono-fin text-sm w-28">
                  {COUNTRY_CODES.map((cc, i) => <option key={`${cc.c}-${i}`} value={cc.d}>{cc.c} {cc.d}</option>)}
                </select>
                <input data-testid="register-phone-input" type="tel" required value={form.phone} onChange={set("phone")}
                  className="flex-1 bg-[#0D0E12] border border-amber-500/20 focus:border-amber-500 rounded-xl px-4 py-3 outline-none" placeholder="Mobile number" />
              </div>
            </div>
            <div>
              <label className="text-sm text-slate-400">Password</label>
              <input data-testid="register-password-input" type="password" required minLength={6} value={form.password} onChange={set("password")}
                className="w-full mt-1.5 bg-[#0D0E12] border border-amber-500/20 focus:border-amber-500 rounded-xl px-4 py-3 outline-none" placeholder="Min 6 characters" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-slate-400">Sponsor Code (optional)</label>
                <input data-testid="register-sponsor-input" value={form.sponsor_code} onChange={set("sponsor_code")}
                  className="w-full mt-1.5 bg-[#0D0E12] border border-amber-500/20 focus:border-amber-500 rounded-xl px-4 py-3 outline-none font-mono-fin" placeholder="MMX..." />
              </div>
              <div>
                <label className="text-sm text-slate-400">Binary Leg {placementLocked && <span className="text-amber-400">(set by sponsor)</span>}</label>
                <select data-testid="register-position-select" value={form.position} onChange={set("position")}
                  className="w-full mt-1.5 bg-[#0D0E12] border border-amber-500/20 focus:border-amber-500 rounded-xl px-4 py-3 outline-none">
                  <option value="left">Left</option>
                  <option value="right">Right</option>
                </select>
              </div>
            </div>
            <button data-testid="register-submit-btn" disabled={busy || !otpSent || unameStatus?.checking || unameStatus?.available === false} className="gold-btn w-full py-3.5 rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-60">
              <UserPlus className="w-5 h-5" /> {busy ? "Creating..." : "Create Account"}
            </button>
          </form>
          <p className="text-sm text-slate-400 mt-6 text-center">
            Already have an account? <Link to="/login" className="text-amber-400 hover:text-amber-300 font-semibold">Sign in</Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:block relative order-1 lg:order-2">
        <img src="https://images.unsplash.com/photo-1636891925388-216a9182ab5b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwyfHxnb2xkJTIwYmFycyUyMGx1eHVyeSUyMHdlYWx0aCUyMGRhcmt8ZW58MHx8fGJsYWNrfDE3ODY1OTkwNTl8MA&ixlib=rb-4.1.0&q=85" alt="wealth" className="w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-l from-[#050507]/40 to-[#050507]" />
        <div className="absolute bottom-16 right-12 max-w-md text-right">
          <h2 className="font-display text-4xl font-extrabold">Smart Choice. <span className="gold-text">Brighter Future.</span></h2>
          <p className="text-slate-300 mt-4">6 income streams. Global markets. One platform.</p>
        </div>
      </div>
    </div>
  );
}
